import {
  Flex, Grid, Image, Stack, rgba,
} from '@mantine/core';
import {
  Button, Text, theme
} from '@huspy/forge';
import useClientDetails from '@modules/clients/entities/client/query/useClientDetails';
import { Link } from '@tanstack/react-router';
import { calc } from '@vanilla-extract/css-utils';
import useVaultProgress from '@modules/vault/hooks/queries/useVaultProgress';
import { imagesPath } from '@shared/css.const';
import { CLIENT_STAGE } from '@modules/clients/entities/client/const';
import { ClientStage } from '@modules/clients/entities/client/client.entity';
import { ClientDetailsRoute } from '@modules/clients/presentation/routes';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import ClientInformationCard from './components/ClientInformationCard';
import { ClientTabs } from './features/ClientTabs';

const shouldFetchVaultProgress = (clientStage?: ClientStage) => {
  switch (clientStage) {
    case CLIENT_STAGE.lead:
    case CLIENT_STAGE.qualified:
    case CLIENT_STAGE.disbursed:
    case CLIENT_STAGE.lost: {
      return false;
    }
    case CLIENT_STAGE.dataCollection:
    case CLIENT_STAGE.dataCollected:
    case CLIENT_STAGE.pendingSignature:
    case CLIENT_STAGE.caseInProgress: {
      return true;
    }
    default: {
      return false;
    }
  }
};

const ClientDetails = () => {
  const { id } = ClientDetailsRoute.useParams();
  const { tab } = ClientDetailsRoute.useSearch();
  const { user } = usePersistUser();
  const { data, isLoading } = useClientDetails(id, user?.brokerageId);

  const { data: { documentProgress } } = useVaultProgress(
    data?.active_opportunity_id,
    shouldFetchVaultProgress(data?.stage)
  );

  if (!isLoading && !data) {
    return (
      <Stack gap={ theme.spacing.xl } justify='center' align='center' h='450px'>
        <Flex direction='column' align='center'>
          <Text c='neutral.6' size='md'>Client not found!</Text>
          <Text c='neutral.6' size='sm'>
            Please return to the Client Hub and create the client again.
          </Text>
        </Flex>
        <Image
          width={ 80 }
          height={ 80 }
          src={ `${imagesPath}/empty-folder.svg` }
        />
        {/* @ts-ignore */}
        <Button component={ Link } color='purple' to='/clients'>Back to Client Hub</Button>
      </Stack>
    );
  }

  return (
    <>
      <Grid
        align='stretch'
        styles={ { inner: { margin: 0 } } }
      >
        <Grid.Col
          mih={ calc('100vh').subtract('64px').toString() }
          p={ theme.spacing.xl }
          styles={ {
            col: {
              backgroundColor: theme.colors.neutral[0],
              borderRight: `1px solid ${theme.colors.neutral[3]}`,
            },
          } }
          span={ 3 }
        >
          <Flex
            gap='sm'
            justify='flex-start'
            align='flex-start'
            direction='column'
            wrap='wrap'
          >
            <Stack gap={ theme.spacing.xl } w='100%'>
              <ClientInformationCard
                data={ data }
                isLoading={ isLoading }
              />
            </Stack>
          </Flex>
        </Grid.Col>
        <Grid.Col
          display='flex'
          styles={ { col: { backgroundColor: rgba(theme.colors.neutral[2], 0.3), flexDirection: 'column' } } }
          span={ 9 }
          p={ `${theme.spacing.xl} ${theme.spacing.xl} 0 ${theme.spacing.xl}` }
        >
          <ClientTabs
            clientActiveOpportunityId={ data?.active_opportunity_id }
            invalidDocumentsCount={ documentProgress.invalid }
            defaultTab={ tab }
          />
        </Grid.Col>
      </Grid>
    </>
  );
};
export default ClientDetails;
